/**
 * Liquidação a comerciantes — o que se lhes deve e a transferência que o paga.
 *
 * O dinheiro está na conta segregada desde o carregamento do cliente. No
 * pagamento mudou de dono, não de sítio. Sai do banco aqui, quando a
 * transferência é feita — e é isso que este ecrã regista.
 *
 * A liquidação ancora no fecho de POS: paga-se 1 a 5 dias depois, conforme o
 * volume. Passar dos 5 aparece assinalado.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Grid2 as Grid, Divider, CircularProgress, Tooltip, IconButton,
  InputAdornment,
} from '@mui/material';
import { Payments, Refresh, ReceiptLong, Warning } from '@mui/icons-material';
import api from '../../../services/api';

interface Pendente {
  entityId: string;
  nome: string;
  codigo: string;
  conta: string | null;
  banco: string | null;
  bancoNome: string | null;
  saldoPendente: number;
  totalRecebido: number;
  ultimaLiquidacao: string | null;
  diasDesdeFecho: number | null;
  emAtraso: boolean;
  temConta: boolean;
}

const cve = (v: number) => `${Number(v || 0).toLocaleString('pt-PT')} CVE`;

const LiquidacaoComerciantes: React.FC = () => {
  const [lista, setLista] = useState<Pendente[]>([]);
  const [aCarregar, setACarregar] = useState(true);
  const [toast, setToast] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false, msg: '', sev: 'success',
  });

  const [extractoAberto, setExtractoAberto] = useState(false);
  const [extracto, setExtracto] = useState<any>(null);
  const [alvo, setAlvo] = useState<Pendente | null>(null);
  const [valor, setValor] = useState('');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [aGuardar, setAGuardar] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [feito, setFeito] = useState<any>(null);

  const mostrar = (msg: string, sev: 'success' | 'error') => setToast({ open: true, msg, sev });

  const carregar = useCallback(async () => {
    setACarregar(true);
    try {
      const r = await api.get('/admin/merchant-settlements');
      setLista(r.data.data || []);
    } catch (e: any) {
      mostrar(e?.response?.data?.error || 'Não foi possível carregar.', 'error');
    } finally {
      setACarregar(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = async (p: Pendente) => {
    setAlvo(p);
    setValor(String(p.saldoPendente));
    setReferencia('');
    setNotas('');
    setExtracto(null);
    setExtractoAberto(true);
    try {
      const r = await api.get(`/admin/merchant-settlements/${p.entityId}`);
      setExtracto(r.data);
    } catch (e: any) {
      mostrar(e?.response?.data?.error || 'Não foi possível carregar o extracto.', 'error');
    }
  };

  const liquidar = async () => {
    if (!alvo) return;
    setAGuardar(true);
    try {
      const r = await api.post('/admin/merchant-settlements', {
        entityId: alvo.entityId,
        valorCVE: Number(valor),
        referencia: referencia || null,
        notas: notas || null,
      });
      // Não fecha o ecrã sem dizer o que fez. A acção lança no livro e é
      // consequente — fechar em silêncio deixa quem a fez sem saber se resultou,
      // e o risco é repetir a transferência por dúvida.
      setConfirmar(false);
      setFeito({
        valor: Number(valor),
        referencia: referencia || '—',
        journalId: r.data?.data?.journalId || null,
        pendenteAntes: Number(extracto?.porLiquidarNoLivro ?? alvo.saldoPendente),
      });
      carregar();
    } catch (e: any) {
      setConfirmar(false);
      mostrar(e?.response?.data?.error || 'Não foi possível registar a liquidação.', 'error');
    } finally {
      setAGuardar(false);
    }
  };

  const totalPendente = lista.reduce((s, l) => s + l.saldoPendente, 0);
  const emAtraso = lista.filter((l) => l.emAtraso).length;
  const divergente = extracto
    && Math.abs(Number(extracto.saldo?.saldoPendente || 0) - Number(extracto.porLiquidarNoLivro || 0)) > 0.01;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>Liquidação a Comerciantes</Typography>
          <Typography variant="body1" color="text.secondary">
            O que se deve a cada comerciante do cashless, já sem a comissão. Paga-se
            1 a 5 dias após o fecho de POS, conforme o volume.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary">Total por liquidar</Typography>
            <Typography variant="h5">{cve(totalPendente)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary">Comerciantes</Typography>
            <Typography variant="h5">{lista.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={emAtraso ? { borderLeft: '4px solid', borderColor: 'warning.main' } : undefined}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Passados 5 dias do fecho</Typography>
              <Typography variant="h5" color={emAtraso ? 'warning.main' : undefined}>{emAtraso}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {aCarregar ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : lista.length === 0 ? (
        <Alert severity="success">Não há nada por liquidar.</Alert>
      ) : (
        <Card>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Comerciante</TableCell>
                  <TableCell align="right">Por liquidar</TableCell>
                  <TableCell align="right">Já recebido</TableCell>
                  <TableCell>Desde o fecho</TableCell>
                  <TableCell>Conta bancária</TableCell>
                  <TableCell align="right">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lista.map((p) => (
                  <TableRow key={p.entityId} hover>
                    <TableCell>
                      <Typography variant="body2">{p.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.codigo}</Typography>
                    </TableCell>
                    <TableCell align="right"><strong>{cve(p.saldoPendente)}</strong></TableCell>
                    <TableCell align="right">{cve(p.totalRecebido)}</TableCell>
                    <TableCell>
                      {p.diasDesdeFecho === null ? '—' : (
                        <Chip
                          size="small" label={`${p.diasDesdeFecho} dias`}
                          color={p.emAtraso ? 'warning' : 'default'}
                          variant={p.emAtraso ? 'filled' : 'outlined'}
                          icon={p.emAtraso ? <Warning /> : undefined}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {p.temConta ? (
                        <Tooltip title={p.bancoNome || ''}>
                          <Box>
                            <Typography variant="body2">{p.banco || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.conta}</Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Sem conta bancária nas Entidades — a transferência não pode ser feita">
                          <Chip size="small" label="Em falta" color="warning" variant="outlined" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<ReceiptLong />} onClick={() => abrir(p)}>
                        Extracto e pagar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {/* ── Extracto e liquidação ────────────────────────────────────────── */}
      <Dialog open={extractoAberto} onClose={() => setExtractoAberto(false)} maxWidth="md" fullWidth>
        <DialogTitle>{alvo?.nome}</DialogTitle>
        <DialogContent dividers>
          {!extracto ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">Por liquidar</Typography>
                  <Typography variant="h6">{cve(extracto.saldo?.saldoPendente)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">No livro</Typography>
                  <Typography variant="h6">{cve(extracto.porLiquidarNoLivro)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">Já recebido</Typography>
                  <Typography variant="h6">{cve(extracto.saldo?.totalRecebido)}</Typography>
                </Grid>
              </Grid>

              {/* Os dois números vêm de sítios diferentes de propósito. Se
                  divergirem, é melhor vê-lo do que ter um só a esconder o outro. */}
              {divergente && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  A conta-corrente e o livro não coincidem. O valor que manda é o do
                  livro — vale a pena perceber a diferença antes de transferir.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Registar transferência</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth size="small" label="Valor" type="number" value={valor}
                    onChange={(e) => setValor(e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">CVE</InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth size="small" label="Referência da transferência"
                    value={referencia} onChange={(e) => setReferencia(e.target.value)}
                    placeholder="Ex.: TRF-2026-0812-001"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" label="Notas" value={notas}
                    onChange={(e) => setNotas(e.target.value)} />
                </Grid>
              </Grid>

              {extracto.fechos?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Últimos fechos de POS</Typography>
                  <Table size="small" sx={{ mb: 2 }}>
                    <TableHead><TableRow>
                      <TableCell>Fecho</TableCell><TableCell align="right">QR</TableCell>
                      <TableCell align="right">NFC</TableCell><TableCell>Quando</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {extracto.fechos.slice(0, 5).map((f: any) => (
                        <TableRow key={f.id}>
                          <TableCell>{f.referencia || '—'}</TableCell>
                          <TableCell align="right">{cve(f.qr_total_cve)}</TableCell>
                          <TableCell align="right">{cve(f.nfc_total_cve)}</TableCell>
                          <TableCell>{f.fecho_at ? new Date(f.fecho_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>Liquidações anteriores</Typography>
              {extracto.liquidacoes?.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma ainda.</Typography>
              ) : (
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Data</TableCell><TableCell align="right">Valor</TableCell>
                    <TableCell>Referência</TableCell><TableCell>Método</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {extracto.liquidacoes.map((l: any) => (
                      <TableRow key={l.id}>
                        <TableCell>{new Date(l.created_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</TableCell>
                        <TableCell align="right">{cve(l.valor)}</TableCell>
                        <TableCell>{l.referencia || '—'}</TableCell>
                        <TableCell><Chip size="small" label={l.metodo} variant="outlined" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtractoAberto(false)}>Fechar</Button>
          <Button
            variant="contained" startIcon={<Payments />} onClick={() => setConfirmar(true)}
            disabled={aGuardar || !valor || Number(valor) <= 0}
          >
            Registar transferência
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação: diz o que vai acontecer, ANTES de acontecer. */}
      <Dialog open={confirmar} onClose={() => setConfirmar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar a transferência</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Vais registar que <strong>{cve(Number(valor))}</strong> foram transferidos para{' '}
            <strong>{alvo?.nome}</strong>
            {referencia ? <> com a referência <strong>{referencia}</strong></> : null}.
          </Typography>
          <Alert severity="info" sx={{ my: 2 }}>
            Ao confirmar, o sistema vai:
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
              <li>lançar no livro o débito da dívida a este comerciante e a saída do banco;</li>
              <li>reduzir o saldo por liquidar de {cve(Number(extracto?.porLiquidarNoLivro ?? 0))} para{' '}
                  {cve(Number(extracto?.porLiquidarNoLivro ?? 0) - Number(valor))};</li>
              <li>guardar a referência para a reconciliação.</li>
            </Box>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Isto regista uma transferência que <strong>já fizeste no banco</strong>. Não
            move dinheiro nenhum — só o inscreve nas contas.
          </Typography>
          {!referencia && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Sem referência da transferência, a reconciliação com o extracto bancário
              fica mais difícil. Podes continuar, mas vale a pena preenchê-la.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)}>Voltar</Button>
          <Button variant="contained" onClick={liquidar} disabled={aGuardar}>
            {aGuardar ? 'A registar…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resumo: o que ficou feito, sem o ecrã desaparecer em silêncio. */}
      <Dialog open={Boolean(feito)} onClose={() => { setFeito(null); setExtractoAberto(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Transferência registada</DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>
            {cve(feito?.valor)} registados para {alvo?.nome}.
          </Alert>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell>Valor</TableCell><TableCell align="right"><strong>{cve(feito?.valor)}</strong></TableCell></TableRow>
              <TableRow><TableCell>Referência</TableCell><TableCell align="right">{feito?.referencia}</TableCell></TableRow>
              <TableRow><TableCell>Por liquidar antes</TableCell><TableCell align="right">{cve(feito?.pendenteAntes)}</TableCell></TableRow>
              <TableRow><TableCell>Por liquidar agora</TableCell><TableCell align="right"><strong>{cve(Number(feito?.pendenteAntes || 0) - Number(feito?.valor || 0))}</strong></TableCell></TableRow>
              {feito?.journalId && (
                <TableRow>
                  <TableCell>Lançamento no livro</TableCell>
                  <TableCell align="right"><Typography variant="caption">{feito.journalId}</Typography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Aparece no extracto do comerciante, em Financeiro → Extracto Comerciantes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setFeito(null); setExtractoAberto(false); }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open} autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast({ ...toast, open: false })}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LiquidacaoComerciantes;
